export interface BreadcrumbItem {
  title: string;
  path: string;
  isActive: boolean;
}

export interface RouteConfig {
  path: string;
  label: string | ((params: Record<string, string>) => string | Promise<string>);
  parent?: string;
  hideInBreadcrumb?: boolean;
}

// Type for dynamic label resolvers
export type DynamicLabelResolver = (
  params: Record<string, string>
) => Promise<string>;

// Registry for dynamic label resolvers
const dynamicLabelResolvers: Map<string, DynamicLabelResolver> = new Map();

/**
 * Register a dynamic label resolver for parametric routes
 * @param routePattern - Route pattern with params (e.g., '/surveys/:id')
 * @param resolver - Async function that fetches and returns the label
 * 
 * @example
 * registerDynamicLabelResolver('/surveys/:surveyId', async (params) => {
 *   const response = await fetch(`/api/surveys/${params.surveyId}`);
 *   const survey = await response.json();
 *   return survey.name || `Survey #${params.surveyId}`;
 * });
 */
export function registerDynamicLabelResolver(
  routePattern: string,
  resolver: DynamicLabelResolver
): void {
  dynamicLabelResolvers.set(routePattern, resolver);
}

/**
 * Extract params from a pathname based on a route pattern
 * @param pathname - Actual path (e.g., '/surveys/123')
 * @param pattern - Route pattern (e.g., '/surveys/:id')
 * @returns Object with extracted params or null if no match
 */
function extractParams(
  pathname: string,
  pattern: string
): Record<string, string> | null {
  const patternSegments = pattern.split('/').filter(Boolean);
  const pathSegments = pathname.split('/').filter(Boolean);

  if (patternSegments.length !== pathSegments.length) {
    return null;
  }

  const params: Record<string, string> = {};
  
  for (let i = 0; i < patternSegments.length; i++) {
    const patternSeg = patternSegments[i];
    const pathSeg = pathSegments[i];
    
    if (patternSeg.startsWith(':')) {
      // This is a parameter
      const paramName = patternSeg.slice(1);
      params[paramName] = pathSeg;
    } else if (patternSeg !== pathSeg) {
      // Mismatch in static segment
      return null;
    }
  }

  return params;
}

// Configure your application routes
export const routes: RouteConfig[] = [
  { path: '/', label: 'Início' },
  { path: '/profile', label: 'Meu Perfil', parent: '/' },
  { path: '/profile/edit', label: 'Editar', parent: '/profile' },
  { path: '/surveys', label: 'Pesquisas', parent: '/' },
  { path: '/engagement', label: 'Pontuação de Engajamento', parent: '/' },
  { path: '/surveys/create', label: 'Criar Pesquisa', parent: '/surveys' },
  { 
    path: '/surveys/:surveyId', 
    label: (params) => `Pesquisa #${params.surveyId}`,
    parent: '/surveys' 
  },
  { 
    path: '/surveys/:surveyId/take', 
    label: 'Responder',
    parent: '/surveys/:surveyId' 
  },
  { 
    path: '/surveys/:surveyId/analytics', 
    label: 'Análises',
    parent: '/surveys/:surveyId' 
  },
  { path: '/analytics', label: 'Análises', parent: '/' },
  { path: '/admin', label: 'Administração', parent: '/' },
  { path: '/admin/users', label: 'Usuários', parent: '/admin' },
  { path: '/admin/surveys', label: 'Pesquisas', parent: '/admin' },
  { path: '/admin/engagement/cycles', label: 'Ciclos de Engajamento', parent: '/admin' },
  { 
    path: '/admin/surveys/:id', 
    label: (params) => `Pesquisa #${params.id}`,
    parent: '/admin/surveys' 
  },
  { 
    path: '/admin/surveys/:id/edit', 
    label: 'Editar',
    parent: '/admin/surveys/:id' 
  },
  { path: '/admin/contractors', label: 'Contratantes', parent: '/admin' },
  { path: '/contractor', label: 'Contratante', parent: '/' },
  { path: '/contractor/surveys', label: 'Minhas Pesquisas', parent: '/contractor' },
  { path: '/contractor/surveys/new', label: 'Nova Pesquisa', parent: '/contractor/surveys' },
  { 
    path: '/contractor/surveys/:surveyId', 
    label: (params) => `Pesquisa #${params.surveyId}`,
    parent: '/contractor/surveys' 
  },
  {
    path: '/contractor/surveys/:id/edit',
    label: 'Editar',
    parent: '/contractor/surveys/:id',
  },
  { path: '/participant', label: 'Participante', parent: '/' },
  { path: '/public', label: 'Pesquisas Públicas', parent: '/' },
  { path: '/public/surveys', label: 'Pesquisas', parent: '/public', hideInBreadcrumb: true },
  {
    path: '/public/surveys/:surveyHash',
    label: 'Pesquisa',
    parent: '/public/surveys',
    hideInBreadcrumb: true,
  },
  {
    path: '/public/surveys/:surveyHash/audit-trail',
    label: 'Trilha de Auditoria',
    parent: '/public/surveys/:surveyHash',
  },
];

/**
 * Generate breadcrumb items from current pathname
 * Supports dynamic routes with parameter extraction
 * @param pathname - Current route path
 * @param routeConfigs - Array of route configurations
 * @param cachedLabels - Optional cache of resolved dynamic labels
 * @returns Array of breadcrumb items
 */
export const generateBreadcrumbs = (
  pathname: string, 
  routeConfigs: RouteConfig[],
  cachedLabels?: Map<string, string>
): BreadcrumbItem[] => {
  const breadcrumbs: BreadcrumbItem[] = [];
  
  // Always start with home
  breadcrumbs.push({
    title: 'Início',
    path: '/',
    isActive: pathname === '/',
  });

  // If we're already at home, return just that
  if (pathname === '/' || pathname === '/home') {
    return breadcrumbs;
  }

  // Build path incrementally
  const pathSegments = pathname.split('/').filter(Boolean);
  let currentPath = '';
  
  pathSegments.forEach((segment, index) => {
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;
    
    // Try to find exact match first
    let config = routeConfigs.find(r => r.path === currentPath);
    let params: Record<string, string> = {};
    
    // If no exact match, try parametric routes
    if (!config) {
      for (const route of routeConfigs) {
        if (route.path.includes(':')) {
          const extractedParams = extractParams(currentPath, route.path);
          if (extractedParams) {
            config = route;
            params = extractedParams;
            break;
          }
        }
      }
    }
    
    // Skip segments marked as hidden
    if (config?.hideInBreadcrumb) return;

    // Determine label
    let label: string;
    if (config) {
      if (typeof config.label === 'function') {
        // Check if we have a cached label
        const cacheKey = currentPath;
        if (cachedLabels?.has(cacheKey)) {
          label = cachedLabels.get(cacheKey)!;
        } else {
          // Call label function with params
          const result = config.label(params);
          label = typeof result === 'string' ? result : `Loading...`;
        }
      } else {
        label = config.label;
      }
    } else {
      // If no config found, capitalize the segment
      label = segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    breadcrumbs.push({
      title: label,
      path: currentPath,
      isActive: isLast,
    });
  });

  return breadcrumbs;
};

/**
 * Async version of generateBreadcrumbs that resolves dynamic labels
 * Use this when you need to fetch resource names from API
 * @param pathname - Current route path
 * @param routeConfigs - Array of route configurations
 * @returns Promise resolving to array of breadcrumb items
 */
export const generateBreadcrumbsAsync = async (
  pathname: string,
  routeConfigs: RouteConfig[]
): Promise<BreadcrumbItem[]> => {
  const breadcrumbs: BreadcrumbItem[] = [];
  
  breadcrumbs.push({
    title: 'Início',
    path: '/',
    isActive: pathname === '/',
  });

  if (pathname === '/' || pathname === '/home') {
    return breadcrumbs;
  }

  const pathSegments = pathname.split('/').filter(Boolean);
  let currentPath = '';
  
  for (let index = 0; index < pathSegments.length; index++) {
    const segment = pathSegments[index];
    currentPath += `/${segment}`;
    const isLast = index === pathSegments.length - 1;
    
    let config = routeConfigs.find(r => r.path === currentPath);
    let params: Record<string, string> = {};
    
    if (!config) {
      for (const route of routeConfigs) {
        if (route.path.includes(':')) {
          const extractedParams = extractParams(currentPath, route.path);
          if (extractedParams) {
            config = route;
            params = extractedParams;
            break;
          }
        }
      }
    }
    
    let label: string;
    if (config) {
      if (typeof config.label === 'function') {
        const result = config.label(params);
        // Await if it's a Promise
        label = await Promise.resolve(result);
      } else {
        label = config.label;
      }
    } else {
      label = segment.charAt(0).toUpperCase() + segment.slice(1);
    }

    breadcrumbs.push({
      title: label,
      path: currentPath,
      isActive: isLast,
    });
  }

  return breadcrumbs;
};
